import { MaskitoMaskExpression, MaskitoOptions, MaskitoPostprocessor, MaskitoPreprocessor } from '@maskito/core';
import { clamp } from 'lodash';

export function withSensitiveMode(
  maskitoOptions: MaskitoOptions,
  options: Partial<{ secureCharacter: string; unmaskLastChars: number }>
) {
  const secureCharacter = options.secureCharacter || '﹡';
  const unmaskLastChars = clamp(options.unmaskLastChars || 0, 0, Infinity);
  const result = { unmasked: '', masked: '' };

  const removeSecureCharacters: MaskitoPreprocessor = ({ elementState, data }) => {
    const value = elementState.value || '';
    const newValue = value
      .split('')
      .map((char, index) => {
        if (char === secureCharacter) {
          return result.unmasked[index] || '';
        }

        return char;
      })
      .join('');

    return {
      data,
      elementState: {
        selection: elementState.selection,
        value: newValue,
      },
    };
  };

  const addSecureCharacters: MaskitoPostprocessor = (elementState) => {
    const value = elementState.value || '';
    result.unmasked = value;
    result.masked = value
      .split('')
      .map((char, index) => {
        if (value.length <= unmaskLastChars) {
          return char;
        }

        if (index >= value.length - unmaskLastChars) {
          return char;
        }

        return secureCharacter;
      })
      .join('');

    return {
      selection: elementState.selection,
      value: result.masked,
    };
  };

  return {
    value: result,
    maskitoOptions: {
      ...maskitoOptions,
      preprocessors: [removeSecureCharacters, ...(maskitoOptions.preprocessors || [])],
      postprocessors: [...(maskitoOptions.postprocessors || []), addSecureCharacters],
    } satisfies MaskitoOptions,
  };
}

export function withCreditCardMask() {
  const maxNumberOfDigits = 16;
  const lastFour = 4;

  return withSensitiveMode(
    { mask: Array.from({ length: maxNumberOfDigits }).map(() => /\d/) },
    { unmaskLastChars: lastFour }
  );
}

export function withGiftCardMask() {
  const maxNumberOfDigits = 20;
  const lastFour = 4;

  return withSensitiveMode(
    { mask: Array.from({ length: maxNumberOfDigits }).map(() => /\d/) },
    { unmaskLastChars: lastFour }
  );
}

export function withCVCMask(): { maskitoOptions: MaskitoOptions } {
  return {
    maskitoOptions: {
      mask: [/\d/, /\d/, /\d/, /\d/],
    } satisfies MaskitoOptions,
  };
}

export function withMonthMask(): { maskitoOptions: MaskitoOptions } {
  const allowedMonths = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

  return {
    maskitoOptions: {
      mask: ({ value }): MaskitoMaskExpression => {
        const digits = value.replace(/\D/g, '').slice(0, 2);
        const stillValid = allowedMonths.some((month) => month.startsWith(digits));

        return stillValid ? [/[0-1]/, /\d/] : [/[0-1]/];
      },
    } satisfies MaskitoOptions,
  };
}

export function withYearMask(): { maskitoOptions: MaskitoOptions } {
  return {
    maskitoOptions: {
      mask: [/\d/, /\d/, /\d/, /\d/],
    } satisfies MaskitoOptions,
  };
}
